import React from "react";

import { Box } from "@material-ui/core";

import HexLevel10Map, { MapCell, pointerInfoEmpty } from "components/visuals/maps/HexLevel10Map";
import useColourPalette from "components/visuals/useColourPalette";
import Explore from "pages/customer/tools/location/map/Explore";
import PinnedLocation from "pages/customer/tools/location/map/PinnedLocation";

import Legend from "./Legend";

import { selectFootfallByAreaMapThresholds, selectFootfallByAreaMeasure, selectFootfallByAreaWithColourGroups } from "modules/customer/tools/location/footfallNew/footfallSlice";
import { selectPinnedLocation } from "modules/customer/tools/location/locationSlice";
import { selectMapboxConfiguration } from "modules/siteConfiguration/siteConfigurationSlice";
import { useAppSelector } from "store";
import { FootfallMeasureType } from "modules/customer/tools/location/footfallNew/footfallMeasureType";

const Map: React.FC = () => {
    const colourPalette = useColourPalette();
    const mapboxConfiguration = useAppSelector(selectMapboxConfiguration);
    const cells = useAppSelector(selectFootfallByAreaWithColourGroups);
    const thresholds = useAppSelector(selectFootfallByAreaMapThresholds);
    const measureType = useAppSelector(selectFootfallByAreaMeasure);
    const retailCentre = useAppSelector(selectPinnedLocation)?.retailCentre;
    const [hoverInfo, setHoverInfo] = React.useState(pointerInfoEmpty);
    const hoveredCell = cells.find(cell => cell.cellId === hoverInfo.cellId);
    const [initialViewport, setInitialViewport] = React.useState({
        latitude: retailCentre?.latitude ?? 0,
        longitude: retailCentre?.longitude ?? 0,
        zoom: 13.5,
        minZoom: 4
    });

    const mapCells: MapCell[] = React.useMemo(() => {
        return cells.map(cell => {
            return {
                id: String(cell.cellId),
                colourGroup: measureType === FootfallMeasureType.NumberOfVisits ? cell.visitsColourGroup : cell.dwellColourGroup,
            };
        });
    }, [cells, measureType]);

    const relevantThresholds: number[] = React.useMemo(() => {
        return measureType === FootfallMeasureType.NumberOfVisits ? thresholds.visitsThresholds : thresholds.dwellThresholds;
    }, [thresholds, measureType]);

    React.useEffect(() => {
        setInitialViewport(current => {
            return {
                ...current,
                latitude: retailCentre?.latitude ?? 0,
                longitude: retailCentre?.longitude ?? 0
            };
        });
    }, [retailCentre]);

    return (
        <Box data-cy="footfall-map">
            <HexLevel10Map
                loading={false}
                error={false}
                mapboxAccessToken={mapboxConfiguration.accessToken}
                mapboxBaseMapStyle={mapboxConfiguration.baseMapStyle}
                mapboxHexLevel10TilesetId={mapboxConfiguration.hex10TilesetId}
                mapboxHexLevel10TilesetUrl={mapboxConfiguration.hex10TilesetUrl}
                height="100vh"
                initialViewport={initialViewport}
                addGeocoder={false}
                cells={mapCells}
                colours={colourPalette.sequential}
                enableHover={true}
                onHover={setHoverInfo}
                enableClick={false}
                displayPOIs={true}
                downloadData={cells}
            >
                <Explore />
                <PinnedLocation />
                <Legend cell={hoveredCell} measureType={measureType} thresholds={relevantThresholds} />
            </HexLevel10Map>
        </Box>
    );
};

export default Map;
